import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api";
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Alert,
  Tooltip,
  Divider,
  Snackbar,
  Tabs,
  Tab,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Delete,
  ContentCopy,
  Add,
  Science,
  History,
  OpenInNew,
  Api,
  Webhook as WebhookIcon,
  MenuBook,
  Forum,
  TableChart,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../AdminLayout";

const KEY_SCOPES = [
  "assets:read",
  "assets:write",
  "undertakings:read",
  "undertakings:write",
  "users:read",
  "users:write",
];

const MAPPING_FIELDS = [
  "externalId",
  "serialNumber",
  "assetTag",
  "model",
  "manufacturer",
  "category",
  "status",
  "site",
  "building",
  "floor",
  "notes",
];

const CHAT_EVENTS = [
  "undertaking.submitted",
  "undertaking.approved",
  "undertaking.rejected",
  "asset.created",
  "asset.assigned",
  "asset.returned",
  "asset.updated",
  "user.upserted",
  "verification.completed",
  "warranty.expiring",
];

const FIELD_PRESETS = {
  intune: {
    label: "Microsoft Intune",
    aliases: {
      externalId: ["AzureADDeviceId", "DeviceId", "IntuneDeviceId"],
      serialNumber: ["SerialNumber", "Serial Number", "BIOSSerialNumber"],
      assetTag: ["DeviceName", "HostName", "ComputerName"],
      model: ["Model", "ModelName"],
      manufacturer: ["Manufacturer", "Make"],
      category: ["DeviceType", "Category"],
    },
  },
  workday: {
    label: "Workday / HR export",
    aliases: {
      // HR exports rarely include assets; keep employee-oriented aliases empty here.
      // Used when spreadsheet mixes assignee columns into asset import.
      notes: ["Worker Comments", "Comments"],
      site: ["Location", "Work Location", "Office"],
      building: ["Building", "Work Space"],
      floor: ["Floor", "Floor Number"],
    },
  },
  jamf: {
    label: "Jamf",
    aliases: {
      externalId: ["Jamf ID", "Computer ID", "UDID"],
      serialNumber: ["Serial Number", "Serial Number (lower)"],
      assetTag: ["Computer Name", "Name"],
      model: ["Model Identifier", "Model"],
      manufacturer: ["Make", "Manufacturer"],
    },
  },
};

const panelSx = {
  p: 2.5,
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
};

function Integrations({ onLogout }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [toast, setToast] = useState("");
  const [apiBase, setApiBase] = useState("");

  const [keyDialog, setKeyDialog] = useState(false);
  const [keyForm, setKeyForm] = useState({
    name: "",
    scopes: [],
    expiresAt: "",
    allowedIps: "",
  });
  const [newKey, setNewKey] = useState(null);

  const [hookDialog, setHookDialog] = useState(false);
  const [hookForm, setHookForm] = useState({ url: "", description: "", events: [] });

  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [chatCfg, setChatCfg] = useState({
    slack: { enabled: false, webhookUrl: "", events: [] },
    teams: { enabled: false, webhookUrl: "", events: [] },
  });
  const [aliasDraft, setAliasDraft] = useState({});

  const curlPing = useMemo(
    () =>
      `curl -s -H "x-api-key: YOUR_API_KEY" \\\n  ${apiBase || "https://api.example.com"}/integrations/v1/ping`,
    [apiBase]
  );

  const curlUpsert = useMemo(
    () =>
      `curl -s -X POST -H "x-api-key: YOUR_API_KEY" -H "Content-Type: application/json" \\\n  ${apiBase || "https://api.example.com"}/integrations/v1/assets \\\n  -d '{"externalId":"INTUNE-123","serialNumber":"SN001","model":"Latitude 5440","category":"Laptop","status":"InStock"}'`,
    [apiBase]
  );

  const loadAll = () => {
    axios.get("/integrations/keys").then((r) => setKeys(r.data.result || [])).catch(() => {});
    axios
      .get("/integrations/webhooks")
      .then((r) => setWebhooks(r.data.result || []))
      .catch(() => {});
    axios.get("/integrations/events").then((r) => setEvents(r.data.result || [])).catch(() => {});
    axios
      .get("/settings")
      .then((r) => {
        const integ = r.data.result?.integrations || {};
        setChatCfg({
          slack: {
            enabled: !!integ.slack?.enabled,
            webhookUrl: integ.slack?.webhookUrl || "",
            events: integ.slack?.events || [],
          },
          teams: {
            enabled: !!integ.teams?.enabled,
            webhookUrl: integ.teams?.webhookUrl || "",
            events: integ.teams?.events || [],
          },
        });
        setAliasDraft(integ.assetFieldAliases || {});
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadAll();
    const origin = (axios.defaults.baseURL || "").replace(/\/$/, "");
    setApiBase(origin || window.location.origin.replace(/:\d+$/, ":5000"));
  }, []);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setToast("Copied to clipboard");
  };

  const toggleScope = (scope) =>
    setKeyForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope)
        ? f.scopes.filter((s) => s !== scope)
        : [...f.scopes, scope],
    }));

  const createKey = async () => {
    try {
      const payload = {
        name: keyForm.name,
        scopes: keyForm.scopes,
        expiresAt: keyForm.expiresAt || null,
        allowedIps: keyForm.allowedIps
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const r = await axios.post("/integrations/keys", payload);
      setNewKey(r.data.apiKey);
      setKeyDialog(false);
      setKeyForm({ name: "", scopes: [], expiresAt: "", allowedIps: "" });
      loadAll();
    } catch (e) {
      setToast(e.response?.data?.message || "Failed to create key");
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? Integrations using it will stop working.")) return;
    await axios.delete(`/integrations/keys/${id}`);
    loadAll();
  };

  const toggleHookEvent = (ev) =>
    setHookForm((f) => ({
      ...f,
      events: f.events.includes(ev)
        ? f.events.filter((e) => e !== ev)
        : [...f.events, ev],
    }));

  const createHook = async () => {
    try {
      await axios.post("/integrations/webhooks", hookForm);
      setHookDialog(false);
      setHookForm({ url: "", description: "", events: [] });
      loadAll();
    } catch (e) {
      setToast(e.response?.data?.message || "Failed to create webhook");
    }
  };

  const toggleHookStatus = async (hook) => {
    await axios.put(`/integrations/webhooks/${hook._id}`, {
      url: hook.url,
      events: hook.events,
      description: hook.description,
      status: hook.status === "Active" ? "Disabled" : "Active",
    });
    loadAll();
  };

  const deleteHook = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await axios.delete(`/integrations/webhooks/${id}`);
    loadAll();
  };

  const testHook = async (id) => {
    setTestingId(id);
    try {
      const r = await axios.post(`/integrations/webhooks/${id}/test`);
      setToast(
        r.data.success
          ? `Test OK (HTTP ${r.data.result?.httpStatus})`
          : `Test failed: ${r.data.result?.error || "error"}`
      );
      loadAll();
    } catch (e) {
      setToast(e.response?.data?.error || "Test failed");
    } finally {
      setTestingId(null);
    }
  };

  const openDeliveries = async (hook) => {
    setDeliveriesFor(hook);
    setDeliveriesOpen(true);
    try {
      const r = await axios.get(`/integrations/webhooks/${hook._id}/deliveries`);
      setDeliveries(r.data.result || []);
    } catch {
      setDeliveries([]);
    }
  };

  const saveIntegrationsSettings = async (patch) => {
    setSettingsSaving(true);
    try {
      await axios.put("/settings", { integrations: patch });
      setToast("Integration settings saved");
      loadAll();
    } catch (e) {
      setToast(e.response?.data?.error || "Failed to save");
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleChatEvent = (channel, ev) => {
    setChatCfg((prev) => {
      const cur = prev[channel] || { events: [] };
      const events = cur.events.includes(ev)
        ? cur.events.filter((e) => e !== ev)
        : [...cur.events, ev];
      return { ...prev, [channel]: { ...cur, events } };
    });
  };

  return (
    <AdminLayout
      title="Integrations"
      subtitle="Connect HR, MDM, ITSM, Zapier/n8n, or any custom system"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Paper elevation={0} sx={{ ...panelSx, mb: 2.5, p: 0, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
            }}
          >
            <Tab icon={<MenuBook fontSize="small" />} iconPosition="start" label="How to connect" />
            <Tab icon={<Api fontSize="small" />} iconPosition="start" label="API keys" />
            <Tab icon={<WebhookIcon fontSize="small" />} iconPosition="start" label="Webhooks" />
            <Tab icon={<Forum fontSize="small" />} iconPosition="start" label="Slack / Teams" />
            <Tab icon={<TableChart fontSize="small" />} iconPosition="start" label="Field mapping" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Connect HRIS, MDM, ITSM, Zapier/n8n, SCIM directories, Slack/Teams, or custom apps.
                Use <strong>API keys</strong> for inbound sync, <strong>webhooks</strong> for
                events, optional <strong>Slack/Teams</strong> adapters, and{" "}
                <strong>field mapping</strong> for spreadsheet imports. Writes accept{" "}
                <code>Idempotency-Key</code> (same key + body returns the original success for
                24h). Keys are rate-limited (default 120/min). See also{" "}
                <code>deploy/CONNECTORS.md</code> for Intune / HR examples.
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  mb: 2.5,
                }}
              >
                {[
                  {
                    title: "1. Create an API key",
                    body: "Pick scopes. Send x-api-key. Optional Idempotency-Key on writes.",
                  },
                  {
                    title: "2. Sync users & assets",
                    body: "POST /users and /assets, or SCIM /scim/v2/Users. Export CSV/JSON via /exports/*.",
                  },
                  {
                    title: "3. Subscribe to webhooks",
                    body: "Receive undertaking.*, asset.*, verification.completed — with retries.",
                  },
                  {
                    title: "4. Optional Slack / Teams",
                    body: "Paste an Incoming Webhook URL — human-readable alerts without Zapier.",
                  },
                ].map((step) => (
                  <Box
                    key={step.title}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.body}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  href={`${apiBase}/integrations/openapi.json`}
                  target="_blank"
                  rel="noreferrer"
                >
                  OpenAPI spec
                </Button>
                <Button variant="outlined" size="small" onClick={() => setTab(1)}>
                  Manage API keys
                </Button>
                <Button variant="outlined" size="small" onClick={() => setTab(2)}>
                  Manage webhooks
                </Button>
              </Stack>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Quick start
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "grey.900",
                  color: "grey.100",
                  overflow: "auto",
                  fontSize: 12,
                  position: "relative",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => copy(curlPing)}
                  sx={{ position: "absolute", top: 4, right: 4, color: "grey.300" }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
                {curlPing}
              </Box>
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "grey.900",
                  color: "grey.100",
                  overflow: "auto",
                  fontSize: 12,
                  position: "relative",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => copy(curlUpsert)}
                  sx={{ position: "absolute", top: 4, right: 4, color: "grey.300" }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
                {curlUpsert}
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    API Keys
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Authenticate machine clients with <code>x-api-key</code> on{" "}
                    <code>/integrations/v1/*</code>.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => setKeyDialog(true)}>
                  New Key
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Scopes</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>IP allowlist</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k._id}>
                      <TableCell>{k.name}</TableCell>
                      <TableCell>
                        <code>{k.prefix}…</code>
                      </TableCell>
                      <TableCell>
                        {(k.scopes || []).map((s) => (
                          <Chip key={s} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        {k.expiresAt
                          ? new Date(k.expiresAt).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        {(k.allowedIps || []).length
                          ? (k.allowedIps || []).join(", ")
                          : "Any"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={k.status}
                          size="small"
                          color={k.status === "Active" ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : "Never"}
                      </TableCell>
                      <TableCell align="right">
                        {k.status === "Active" && (
                          <Tooltip title="Revoke">
                            <IconButton size="small" color="error" onClick={() => revokeKey(k._id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {keys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No API keys yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Webhooks
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Signed POSTs with automatic retries. Disabled after 10 consecutive failures.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => setHookDialog(true)}>
                  New Webhook
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>URL</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Secret</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Delivery</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.map((h) => (
                    <TableRow key={h._id}>
                      <TableCell sx={{ maxWidth: 220, wordBreak: "break-all" }}>
                        {h.url}
                        {h.description && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {h.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {(h.events || []).map((e) => (
                          <Chip key={e} label={e} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Copy secret">
                          <IconButton size="small" onClick={() => copy(h.secret)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={h.status}
                          size="small"
                          color={h.status === "Active" ? "success" : "default"}
                          onClick={() => toggleHookStatus(h)}
                        />
                        {h.failureCount > 0 && (
                          <Typography variant="caption" display="block" color="error">
                            {h.failureCount} fail(s)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {h.lastDeliveryAt ? (
                          <>
                            <Chip
                              label={h.lastStatus || "err"}
                              size="small"
                              color={
                                h.lastStatus >= 200 && h.lastStatus < 300 ? "success" : "error"
                              }
                            />
                            <Typography variant="caption" display="block" color="text.secondary">
                              {new Date(h.lastDeliveryAt).toLocaleString()}
                            </Typography>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Send test ping">
                          <IconButton
                            size="small"
                            disabled={testingId === h._id}
                            onClick={() => testHook(h._id)}
                          >
                            <Science fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delivery history">
                          <IconButton size="small" onClick={() => openDeliveries(h)}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => deleteHook(h._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {webhooks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No webhooks yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {tab === 3 && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Paste Incoming Webhook URLs from Slack or Microsoft Teams. Selected events post a
                short human-readable message (in addition to generic webhooks).
              </Alert>
              {["slack", "teams"].map((channel) => (
                <Box key={channel} sx={{ ...panelSx, mb: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                      {channel === "slack" ? "Slack" : "Microsoft Teams"}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!chatCfg[channel]?.enabled}
                          onChange={(e) =>
                            setChatCfg((prev) => ({
                              ...prev,
                              [channel]: { ...prev[channel], enabled: e.target.checked },
                            }))
                          }
                        />
                      }
                      label="Enabled"
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    label="Incoming webhook URL"
                    value={chatCfg[channel]?.webhookUrl || ""}
                    onChange={(e) =>
                      setChatCfg((prev) => ({
                        ...prev,
                        [channel]: { ...prev[channel], webhookUrl: e.target.value },
                      }))
                    }
                    sx={{ mb: 1.5 }}
                    placeholder={
                      channel === "slack"
                        ? "https://hooks.slack.com/services/..."
                        : "https://outlook.office.com/webhook/..."
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Events (empty = none until you pick some)
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 0.25,
                      mb: 1,
                    }}
                  >
                    {CHAT_EVENTS.map((ev) => (
                      <FormControlLabel
                        key={ev}
                        control={
                          <Checkbox
                            size="small"
                            checked={(chatCfg[channel]?.events || []).includes(ev)}
                            onChange={() => toggleChatEvent(channel, ev)}
                          />
                        }
                        label={ev}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              <Button
                variant="contained"
                disabled={settingsSaving}
                onClick={() =>
                  saveIntegrationsSettings({
                    ...chatCfg,
                    assetFieldAliases: aliasDraft,
                  })
                }
              >
                {settingsSaving ? "Saving…" : "Save Slack / Teams"}
              </Button>
            </Box>
          )}

          {tab === 4 && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Map spreadsheet / MDM column headers to Asset fields. Enter comma-separated
                aliases (e.g. <code>Device Serial, BIOS Serial</code> for serialNumber). Used by
                Inventory bulk import. Apply a preset to start from Intune / Jamf / Workday
                headers.
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {Object.entries(FIELD_PRESETS).map(([key, preset]) => (
                  <Button
                    key={key}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setAliasDraft((prev) => ({
                        ...prev,
                        ...preset.aliases,
                      }))
                    }
                  >
                    {preset.label} preset
                  </Button>
                ))}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  mb: 2,
                }}
              >
                {MAPPING_FIELDS.map((field) => (
                  <TextField
                    key={field}
                    size="small"
                    label={field}
                    fullWidth
                    value={(aliasDraft[field] || []).join(", ")}
                    onChange={(e) => {
                      const aliases = e.target.value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean);
                      setAliasDraft((prev) => ({ ...prev, [field]: aliases }));
                    }}
                    placeholder="Alias1, Alias2"
                  />
                ))}
              </Box>
              <Button
                variant="contained"
                disabled={settingsSaving}
                onClick={() =>
                  saveIntegrationsSettings({
                    ...chatCfg,
                    assetFieldAliases: aliasDraft,
                  })
                }
              >
                {settingsSaving ? "Saving…" : "Save field mapping"}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      <Dialog open={keyDialog} onClose={() => setKeyDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={keyForm.name}
            onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
            placeholder="e.g. HRMS sync / Intune / Zapier"
          />
          <TextField
            label="Expires at (optional)"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={keyForm.expiresAt}
            onChange={(e) => setKeyForm({ ...keyForm, expiresAt: e.target.value })}
          />
          <TextField
            label="IP allowlist (optional)"
            fullWidth
            margin="normal"
            value={keyForm.allowedIps}
            onChange={(e) => setKeyForm({ ...keyForm, allowedIps: e.target.value })}
            placeholder="Comma-separated IPs or CIDRs, e.g. 10.0.0.5, 203.0.113.0/24"
            helperText="Leave blank to allow any IP"
          />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Scopes
          </Typography>
          <FormGroup>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 0.5,
              }}
            >
              {KEY_SCOPES.map((s) => (
                <FormControlLabel
                  key={s}
                  control={
                    <Checkbox
                      checked={keyForm.scopes.includes(s)}
                      onChange={() => toggleScope(s)}
                    />
                  }
                  label={s}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createKey} disabled={!keyForm.name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!newKey} onClose={() => setNewKey(null)} fullWidth maxWidth="sm">
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it won&apos;t be shown again.
          </Alert>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              bgcolor: "grey.100",
              borderRadius: 1,
              wordBreak: "break-all",
            }}
          >
            <code style={{ flexGrow: 1 }}>{newKey}</code>
            <IconButton size="small" onClick={() => copy(newKey)}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewKey(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={hookDialog} onClose={() => setHookDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Webhook</DialogTitle>
        <DialogContent>
          <TextField
            label="Endpoint URL"
            fullWidth
            margin="normal"
            value={hookForm.url}
            onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })}
            placeholder="https://hooks.zapier.com/... or https://your.app/webhooks"
          />
          <TextField
            label="Description (optional)"
            fullWidth
            margin="normal"
            value={hookForm.description}
            onChange={(e) => setHookForm({ ...hookForm, description: e.target.value })}
          />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Events
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 0.25,
            }}
          >
            {events.map((ev) => (
              <FormControlLabel
                key={ev}
                control={
                  <Checkbox
                    checked={hookForm.events.includes(ev)}
                    onChange={() => toggleHookEvent(ev)}
                  />
                }
                label={ev}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHookDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={createHook}
            disabled={!hookForm.url || hookForm.events.length === 0}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deliveriesOpen}
        onClose={() => setDeliveriesOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Delivery history
          {deliveriesFor && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              {deliveriesFor.url}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Attempt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>HTTP</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map((d) => (
                <TableRow key={d._id}>
                  <TableCell>{new Date(d.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{d.event}</TableCell>
                  <TableCell>{d.attempt}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={d.status}
                      color={d.status === "success" ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell>{d.httpStatus ?? "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                    {d.error || "—"}
                  </TableCell>
                </TableRow>
              ))}
              {deliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No deliveries yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveriesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        message={toast}
      />
    </AdminLayout>
  );
}

export default Integrations;
